import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import styles from '../../styles/BookingModal.module.css';
import { supabase } from '../../lib/supabase';
import { Screening } from '../../types';
import { Session } from '@supabase/supabase-js';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import WalletPaymentSection from './WalletPaymentSection';
import StripeCheckoutForm from './StripeCheckoutForm';

interface PaymentProps {
  totalAmount: number;
  totalWithFee: number;
  acceptedTerms: boolean;
  checkoutInitiated: boolean;
  setCheckoutInitiated: React.Dispatch<React.SetStateAction<boolean>>;
  handleBooking: (paymentDetails: PaymentSuccess) => Promise<void>;
  session: Session | null;
  selectedScreening: Screening | null;
  totalTickets: number;
  seatPrices: { silver: number; gold: number; diamond: number };
  setShowAuthModal?: React.Dispatch<React.SetStateAction<boolean>>;
  initiateCheckout?: () => Promise<void>;
  seatsAvailable?: boolean;
  movieTitle?: string;
  selectedSeatIds?: string[];
  customerName?: string;
  phoneNumber?: string;
}

interface PaymentSuccess {
  payment_intent_id: string;
  amount: number;
  status: string;
}

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string);

const Payment = ({
  totalAmount,
  totalWithFee,
  acceptedTerms,
  checkoutInitiated,
  setCheckoutInitiated,
  handleBooking,
  session,
  selectedScreening,
  totalTickets,
  seatPrices,
  setShowAuthModal,
  initiateCheckout,
  seatsAvailable = true,
  movieTitle,
  selectedSeatIds,
  customerName,
  phoneNumber
}: PaymentProps) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [creatingIntent, setCreatingIntent] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    const createIntent = async () => {
      if (!session || !selectedScreening || !checkoutInitiated) return;
      try {
        setCreatingIntent(true);

        // Debug logging
        console.log('[PaymentComponent] Creating payment intent with:', {
          totalWithFee,
          totalTickets,
          selectedSeatIds: selectedSeatIds?.length || 0,
          amount: Math.round(totalWithFee * 100),
        });

        if (!totalWithFee || totalWithFee <= 0) {
          console.error('[PaymentComponent] Invalid totalWithFee:', totalWithFee);
          toast.error('Invalid payment amount. Please try again.');
          setCheckoutInitiated(false);
          return;
        }

        const { data, error } = await supabase.functions.invoke<{ clientSecret: string }>('create-payment-intent', {
          body: {
            amount: Math.round(totalWithFee * 100),
            currency: 'eur',
            methodTypes: ['card'],
            captureMethod: (() => {
              const start = selectedScreening.start_time ? new Date(selectedScreening.start_time).getTime() : Date.now()
              const diffMs = start - Date.now()
              const diffHours = diffMs / (1000 * 60 * 60)
              return diffHours > 24 ? 'manual' : 'automatic'
            })(),
            metadata: {
              screening_id: selectedScreening.id,
              seats: totalTickets,
              user_id: session.user.id,
              movie_name: movieTitle || 'Unknown Movie',
              seat_ids: selectedSeatIds ? selectedSeatIds.join(', ') : 'Unassigned',
              customer_name: customerName || session.user.user_metadata?.full_name || 'Guest',
              phone_number: phoneNumber || session.user.phone || '',
              screening_date: selectedScreening.start_time,
              location: selectedScreening.location,
            },
          },
        });
        if (error) {
          console.error('Payment intent creation failed:', error);
          let errorMessage = 'Failed to initialize payment';
          if (error instanceof Error) {
            errorMessage = error.message;
            // @ts-ignore - Supabase functions error might have context
            if (error.context && error.context.json) {
              // @ts-ignore
              const details = await error.context.json();
              if (details.details) errorMessage += `: ${details.details}`;
              else if (details.error) errorMessage += `: ${details.error}`;
            }
          }
          toast.error(errorMessage);
          setCheckoutInitiated(false);
          return;
        }
        setClientSecret(data?.clientSecret || null);
      } finally {
        setCreatingIntent(false);
      }
    };
    createIntent();
  }, [checkoutInitiated, session, selectedScreening, totalWithFee, totalTickets, setCheckoutInitiated, movieTitle, selectedSeatIds]);

  return (
    <>
      <div className={styles.priceInfo}>
        {session ? (
          <>
            <p className={styles.priceText}>Total: €{totalWithFee.toFixed(2)}</p>
          </>
        ) : (
          <p className={styles.priceText}>Price: €{totalAmount.toFixed(2)}</p>
        )}
      </div>

      {session ? (
        selectedScreening ? (
          <div className={styles.paypalContainer}>
            {!checkoutInitiated && (
              <button
                onClick={() => {
                  if (initiateCheckout) {
                    initiateCheckout();
                  } else if (!acceptedTerms) {
                    toast.error('You must accept the terms and conditions to proceed');
                  } else if (!seatsAvailable) {
                    toast.error('Selected seats are no longer available');
                  } else {
                    setCheckoutInitiated(true);
                  }
                }}
                disabled={!seatsAvailable || totalTickets === 0}
                className={`${styles.button} ${styles.primaryButton} ${(!seatsAvailable || totalTickets === 0) ? styles.disabledButton : ''}`}
              >
                Checkout
              </button>
            )}

            {checkoutInitiated && (
              <div>
                {creatingIntent && !clientSecret && (
                  <p className={styles.priceText}>Initializing payment...</p>
                )}
                {clientSecret && (
                  <div>
                    <WalletPaymentSection
                      stripePromise={stripePromise}
                      clientSecret={clientSecret}
                      totalWithFee={totalWithFee}
                      movieTitle={movieTitle}
                      totalTickets={totalTickets}
                      completed={completed}
                      setCompleted={setCompleted}
                      handleBooking={handleBooking}
                    />

                    {/* Regular Card Payment Form - Separate Elements */}
                    <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'night' } }}>
                      <StripeCheckoutForm onSuccess={async (details) => {
                        if (completed) return;
                        setCompleted(true);
                        // Don't close checkout yet, let handleBooking do it
                        await handleBooking(details);
                      }} />
                    </Elements>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <p className={styles.signInMessage}>Please select a screening to proceed with payment</p>
        )
      ) : (
        <div className="text-center">
          <p className={styles.signInMessage}>Please sign in to book tickets</p>
          <button
            onClick={() => setShowAuthModal && setShowAuthModal(true)}
            className={`${styles.button} ${styles.primaryButton} ${styles.attentionGlow}`}
          >
            Sign In
          </button>
        </div>
      )}
    </>
  );
};

export default Payment;
